/* Nerdio Wishlist — grouped, saved wishlists (parallel to compare.js).
 *
 * localStorage shape (nerdio:wishlist:v2):
 *   { groups: [{ id, name, handles: [] }], activeId }
 * Migrates the legacy flat array (nerdio:wishlist) into the first group.
 * Up to MAX_GROUPS saved wishlists, kept until deleted; no per-group cap.
 *
 * Public API (window.NerdioWishlist):
 *   read() → ACTIVE group's handles (array) — back-compat for the full page.
 *   has/add/remove/toggle/clear act on the ACTIVE group.
 *   count() = active group size; totalCount() = sum across all groups (header badge).
 *   groups/createGroup/renameGroup/deleteGroup/setActive + open() (modal).
 * Hydrates [data-wishlist-toggle="<handle>"] + [data-wishlist-count]; dispatches
 * `wishlist:change` on every mutation. Modal = grid viewer + group manager,
 * reusing the .cmp-modal shell (styled in snippets/compare-bar.liquid). */
(function () {
  'use strict';

  var KEY = 'nerdio:wishlist:v2';
  var LEGACY_KEY = 'nerdio:wishlist';
  var MAX_GROUPS = 12;
  var WL_PATH = '/pages/wishlist';
  var editingId = null;

  var ICON_PENCIL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>';
  var ICON_COG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>';
  var ICON_TRASH = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M10 11v6M14 11v6"/></svg>';
  var ICON_MINUS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M8 12h8"/></svg>';
  var ICON_SHARE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/></svg>';
  var ICON_COPY = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';

  function uid() { return 'w' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }
  function newGroup(name) { return { id: uid(), name: name || 'My wishlist', handles: [] }; }

  /* ---- Store (grouped) ---- */
  function normalize(s) {
    if (!s || !Array.isArray(s.groups) || !s.groups.length) return null;
    s.groups = s.groups.map(function (g) {
      return {
        id: g.id || uid(),
        name: g.name || 'Wishlist',
        handles: (Array.isArray(g.handles) ? g.handles : [])
          .filter(function (h) { return typeof h === 'string' && h; })
      };
    });
    if (!s.activeId || !s.groups.some(function (g) { return g.id === s.activeId; })) s.activeId = s.groups[0].id;
    return s;
  }
  function readState() {
    try {
      var raw = localStorage.getItem(KEY);
      var s = raw ? normalize(JSON.parse(raw)) : null;
      if (s) return s;
      var legacy = localStorage.getItem(LEGACY_KEY); // one-time migration
      if (legacy) {
        var arr = JSON.parse(legacy);
        if (Array.isArray(arr) && arr.length) {
          var g = newGroup('My wishlist');
          g.handles = arr.filter(function (h) { return typeof h === 'string' && h; });
          return { groups: [g], activeId: g.id };
        }
      }
    } catch (e) { /* fall through */ }
    var d = newGroup('My wishlist');
    return { groups: [d], activeId: d.id };
  }
  function write(s) {
    try { localStorage.setItem(KEY, JSON.stringify(s)); } catch (e) { /* quota / private mode */ }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { state: s, list: activeGroup(s).handles.slice() } }));
  }

  function activeGroup(s) {
    s = s || readState();
    return s.groups.filter(function (g) { return g.id === s.activeId; })[0] || s.groups[0];
  }

  /* ---- Active-group accessors (public API surface) ---- */
  function read() { return activeGroup().handles.slice(); }
  function has(handle) { return activeGroup().handles.indexOf(handle) !== -1; }
  function count() { return activeGroup().handles.length; }
  function totalCount() { return readState().groups.reduce(function (n, g) { return n + g.handles.length; }, 0); }

  function add(handle) {
    var s = readState(); var g = activeGroup(s);
    if (g.handles.indexOf(handle) === -1) { g.handles.push(handle); write(s); }
    return read();
  }
  function remove(handle) {
    var s = readState(); var g = activeGroup(s);
    g.handles = g.handles.filter(function (h) { return h !== handle; }); write(s);
    return read();
  }
  function toggle(handle) { return has(handle) ? remove(handle) : add(handle); }
  function clearActive() { var s = readState(); activeGroup(s).handles = []; write(s); }
  function clearGroup(id) {
    var s = readState(); var g = s.groups.filter(function (x) { return x.id === id; })[0];
    if (g) { g.handles = []; write(s); }
  }
  function duplicateGroup(id) {
    var s = readState();
    if (s.groups.length >= MAX_GROUPS) { announce('Maximum ' + MAX_GROUPS + ' saved wishlists.'); return; }
    var src = s.groups.filter(function (x) { return x.id === id; })[0];
    if (!src) return;
    var g = newGroup((src.name + ' copy').slice(0, 40));
    g.handles = src.handles.slice();
    s.groups.push(g); s.activeId = g.id; write(s);
  }

  /* ---- Announcer (reuses the global .cmp-announce toast) ---- */
  var announceEl;
  function announce(msg) {
    if (!announceEl) {
      announceEl = document.createElement('div');
      announceEl.setAttribute('role', 'status'); announceEl.setAttribute('aria-live', 'polite');
      announceEl.className = 'cmp-announce'; document.body.appendChild(announceEl);
    }
    announceEl.textContent = ''; void announceEl.offsetWidth; announceEl.textContent = msg;
    announceEl.classList.add('is-shown');
    clearTimeout(announce._t);
    announce._t = setTimeout(function () { announceEl.classList.remove('is-shown'); }, 2600);
  }

  /* ---- Share (encode a group's handles into a /pages/wishlist link) ---- */
  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).then(function () { return true; }, function () { return fallbackCopy(text); });
    }
    return Promise.resolve(fallbackCopy(text));
  }
  function fallbackCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      var ok = document.execCommand('copy'); document.body.removeChild(ta);
      return ok;
    } catch (e) { return false; }
  }
  function shareGroup(id) {
    var g = readState().groups.filter(function (x) { return x.id === id; })[0];
    if (!g || !g.handles.length) { announce('Save products before sharing.'); return; }
    var url = location.origin + WL_PATH + '?wishlist=' + encodeURIComponent(g.handles.join(','));
    copyToClipboard(url).then(function (ok) { announce(ok ? 'Wishlist link copied to clipboard' : url); });
  }
  function importShared() {
    try {
      var params = new URLSearchParams(location.search);
      var raw = params.get('wishlist');
      if (!raw) return;
      var handles = decodeURIComponent(raw).split(',').map(function (h) { return h.trim(); }).filter(Boolean);
      params.delete('wishlist');
      var qs = params.toString();
      history.replaceState(null, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
      if (!handles.length) return;
      var s = readState();
      if (s.groups.length >= MAX_GROUPS) s.groups.pop();
      var g = newGroup('Shared wishlist'); g.handles = handles;
      s.groups.push(g); s.activeId = g.id; write(s);
    } catch (e) { /* ignore malformed */ }
  }

  function createGroup(name) {
    var s = readState();
    if (s.groups.length >= MAX_GROUPS) return null;
    var g = newGroup(name || ('Wishlist ' + (s.groups.length + 1)));
    s.groups.push(g); s.activeId = g.id; write(s); return g.id;
  }
  function renameGroup(id, name) {
    var s = readState(); var g = s.groups.filter(function (x) { return x.id === id; })[0];
    if (g) { g.name = (name || '').trim() || g.name; write(s); }
  }
  function deleteGroup(id) {
    var s = readState();
    s.groups = s.groups.filter(function (g) { return g.id !== id; });
    if (!s.groups.length) s.groups = [newGroup('My wishlist')];
    if (s.activeId === id || !s.groups.some(function (g) { return g.id === s.activeId; })) s.activeId = s.groups[0].id;
    write(s);
  }
  function setActive(id) { var s = readState(); if (s.groups.some(function (g) { return g.id === id; })) { s.activeId = id; write(s); } }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* ---- Hydration ---- */
  function syncToggle(btn) {
    var handle = btn.getAttribute('data-wishlist-toggle');
    if (!handle) return;
    var active = has(handle);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    var label = active
      ? btn.getAttribute('data-label-remove') || 'Remove from wishlist'
      : btn.getAttribute('data-label-add') || 'Add to wishlist';
    btn.setAttribute('aria-label', label);
  }
  function syncAllToggles() { document.querySelectorAll('[data-wishlist-toggle]').forEach(syncToggle); }

  function syncCounts() {
    var n = totalCount();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = String(n);
      el.toggleAttribute('hidden', n === 0);
    });
  }
  function syncHeader() {
    var has_items = totalCount() > 0;
    document.querySelectorAll('.site-header__wishlist').forEach(function (el) {
      el.classList.toggle('has-items', has_items);
    });
  }

  function onToggleClick(e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    toggle(btn.getAttribute('data-wishlist-toggle'));
  }

  /* ---- Modal: grid viewer + group manager (reuses the .cmp-modal shell) ---- */
  var modal, modalBody, modalGroups, modalReturnFocus, wlToken;

  function ensureModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'cmp-modal';
    modal.hidden = true;
    modal.innerHTML =
      '<div class="cmp-modal__overlay" data-wl-close></div>'
      + '<div class="cmp-modal__panel" role="dialog" aria-modal="true" aria-label="Your wishlist">'
      +   '<div class="cmp-modal__head">'
      +     '<h2 class="cmp-modal__title">Wishlist</h2>'
      +     '<a class="wl-modal__viewall" href="' + WL_PATH + '">Full page'
      +       '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg></a>'
      +     '<button type="button" class="cmp-modal__close" data-wl-close aria-label="Close">&times;</button>'
      +   '</div>'
      +   '<div class="cmp-modal__layout">'
      +     '<aside class="cmp-modal__aside" data-wl-groups></aside>'
      +     '<div class="cmp-modal__main" data-wl-body></div>'
      +   '</div>'
      + '</div>';
    document.body.appendChild(modal);
    modalBody = modal.querySelector('[data-wl-body]');
    modalGroups = modal.querySelector('[data-wl-groups]');

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-wl-close]')) { e.preventDefault(); closeModal(); }
    });
    return modal;
  }

  /* ---- Manager interaction (document-delegated: works in the modal AND on the
     full wishlist page) ---- */
  function onManagerClick(e) {
    var sw = e.target.closest('[data-wl-switch]');
    if (sw) { closeMenu(); setActive(sw.getAttribute('data-wl-switch')); return; }
    var ed = e.target.closest('[data-wl-edit]');
    if (ed) { e.preventDefault(); closeMenu(); enterEdit(ed.getAttribute('data-wl-edit')); return; }
    var cog = e.target.closest('[data-wl-cog]');
    if (cog) { e.preventDefault(); e.stopPropagation(); toggleMenu(cog, cog.getAttribute('data-wl-cog')); return; }
    if (e.target.closest('[data-wl-new]')) { closeMenu(); createGroup(); return; }
  }
  function onManagerFocusout(e) {
    var inp = e.target.closest('[data-wl-rename]');
    if (!inp) return;
    var id = inp.getAttribute('data-wl-rename');
    var val = inp.value;
    editingId = null;
    renameGroup(id, val);
  }
  function onDocKeydown(e) {
    if (e.key === 'Escape') {
      if (editingId) { editingId = null; renderManager(); return; }
      if (popmenu) { closeMenu(); return; }
      if (modal && !modal.hidden) closeModal();
      return;
    }
    if (e.key === 'Enter') {
      var inp = e.target.closest('[data-wl-rename]');
      if (inp) { e.preventDefault(); inp.blur(); }
      return;
    }
    if (e.key === 'Tab' && modal && !modal.hidden && !popmenu) trapFocus(e);
  }
  // Keep keyboard focus inside the open modal panel.
  function trapFocus(e) {
    var panel = modal.querySelector('.cmp-modal__panel');
    if (!panel) return;
    var nodes = panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])');
    var f = Array.prototype.filter.call(nodes, function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function enterEdit(id) {
    editingId = id;
    renderManager();
    focusVisibleRename();
  }

  var popmenu, popAnchor;
  function onDocMenuClose(e) {
    if (popmenu && (popmenu.contains(e.target) || (popAnchor && popAnchor.contains(e.target)))) return;
    closeMenu();
  }
  function closeMenu() {
    if (popAnchor) popAnchor.setAttribute('aria-expanded', 'false');
    popAnchor = null;
    if (popmenu) { popmenu.remove(); popmenu = null; }
    document.removeEventListener('click', onDocMenuClose, true);
    window.removeEventListener('resize', closeMenu);
    document.removeEventListener('scroll', closeMenu, true);
  }
  function toggleMenu(anchor, id) {
    if (popAnchor === anchor) { closeMenu(); return; }
    openMenu(anchor, id);
  }
  function openMenu(anchor, id) {
    closeMenu();
    popAnchor = anchor;
    anchor.setAttribute('aria-expanded', 'true');
    popmenu = document.createElement('div');
    popmenu.className = 'cmp-popmenu';
    popmenu.innerHTML =
      '<button type="button" class="cmp-popmenu__item" data-wl-share="' + escapeHtml(id) + '">' + ICON_SHARE + ' Share link</button>'
      + '<button type="button" class="cmp-popmenu__item" data-wl-dup="' + escapeHtml(id) + '">' + ICON_COPY + ' Duplicate</button>'
      + '<button type="button" class="cmp-popmenu__item" data-wl-clear-group="' + escapeHtml(id) + '">' + ICON_MINUS + ' Empty list</button>'
      + '<button type="button" class="cmp-popmenu__item cmp-popmenu__item--danger" data-wl-del="' + escapeHtml(id) + '">' + ICON_TRASH + ' Delete wishlist</button>';
    document.body.appendChild(popmenu);
    popmenu.addEventListener('click', function (e) {
      var sh = e.target.closest('[data-wl-share]');
      if (sh) { var sid = sh.getAttribute('data-wl-share'); closeMenu(); shareGroup(sid); return; }
      var dup = e.target.closest('[data-wl-dup]');
      if (dup) { var uid2 = dup.getAttribute('data-wl-dup'); closeMenu(); duplicateGroup(uid2); return; }
      var clr = e.target.closest('[data-wl-clear-group]');
      if (clr) { var cid = clr.getAttribute('data-wl-clear-group'); closeMenu(); clearGroup(cid); return; }
      var del = e.target.closest('[data-wl-del]');
      if (del) { var did = del.getAttribute('data-wl-del'); closeMenu(); deleteGroup(did); return; }
    });
    var r = anchor.getBoundingClientRect();
    var mw = popmenu.offsetWidth, mh = popmenu.offsetHeight;
    var left = Math.max(8, Math.min(r.right - mw, window.innerWidth - mw - 8));
    var top = r.bottom + 6;
    if (top + mh > window.innerHeight - 8) top = Math.max(8, r.top - mh - 6);
    popmenu.style.left = left + 'px';
    popmenu.style.top = top + 'px';
    setTimeout(function () { document.addEventListener('click', onDocMenuClose, true); }, 0);
    window.addEventListener('resize', closeMenu);
    document.addEventListener('scroll', closeMenu, true);
  }

  function buildManagerHTML(s) {
    var html = '<div class="cmp-aside__head">Wishlists</div><div class="cmp-aside__list">';
    s.groups.forEach(function (g) {
      var isActive = g.id === s.activeId;
      html += '<div class="cmp-group' + (isActive ? ' is-active' : '') + '">';
      if (editingId === g.id) {
        html += '<input class="cmp-group__input" data-wl-rename="' + escapeHtml(g.id) + '" value="' + escapeHtml(g.name) + '" maxlength="40" aria-label="Rename wishlist">';
      } else if (isActive) {
        html += '<span class="cmp-group__name">' + escapeHtml(g.name) + '</span>';
      } else {
        html += '<button type="button" class="cmp-group__name" data-wl-switch="' + escapeHtml(g.id) + '">' + escapeHtml(g.name) + '</button>';
      }
      html += '<span class="cmp-group__count">' + g.handles.length + '</span>';
      if (editingId !== g.id) {
        html += '<div class="cmp-group__tools">'
          + '<button type="button" class="cmp-group__tool" data-wl-edit="' + escapeHtml(g.id) + '" title="Rename" aria-label="Rename ' + escapeHtml(g.name) + '">' + ICON_PENCIL + '</button>'
          + '<button type="button" class="cmp-group__tool" data-wl-cog="' + escapeHtml(g.id) + '" title="Options" aria-haspopup="true" aria-expanded="false" aria-label="Options for ' + escapeHtml(g.name) + '">' + ICON_COG + '</button>'
          + '</div>';
      }
      html += '</div>';
    });
    html += '</div>';
    html += '<button type="button" class="cmp-aside__new" data-wl-new'
      + (s.groups.length >= MAX_GROUPS ? ' disabled' : '')
      + '><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg> New wishlist</button>';
    return html;
  }
  function focusVisibleRename() {
    var inps = document.querySelectorAll('[data-wl-rename]');
    for (var i = 0; i < inps.length; i++) { if (inps[i].offsetParent !== null) { inps[i].focus(); inps[i].select(); return; } }
    if (inps[0]) { inps[0].focus(); inps[0].select(); }
  }
  /* Renders the manager into every container — the modal aside AND the optional
     full wishlist page aside ([data-wl-groups]). */
  function renderManager() {
    var containers = document.querySelectorAll('[data-wl-groups]');
    if (!containers.length) return;
    var html = buildManagerHTML(readState());
    containers.forEach(function (c) { c.innerHTML = html; });
    if (editingId) focusVisibleRename();
  }

  function fetchCard(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '?view=card-fragment', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (r) { return r.ok ? r.text() : null; }).catch(function () { return null; });
  }

  function emptyState() {
    return '<div class="wl-modal__empty">'
      + '<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>'
      + '<p class="wl-modal__empty-title">This wishlist is empty</p>'
      + '<p>Tap the heart on any product to save it here.</p>'
      + '</div>';
  }

  function renderModal() {
    if (!modal || modal.hidden) return;
    renderManager();
    var handles = activeGroup().handles;
    if (!handles.length) { modalBody.innerHTML = emptyState(); return; }
    var token = (wlToken = {});
    Promise.all(handles.map(fetchCard)).then(function (htmls) {
      if (token !== wlToken || !modal || modal.hidden) return;
      var cards = htmls.filter(function (h) { return h && h.trim(); }).join('');
      if (!cards) { modalBody.innerHTML = emptyState(); return; }
      modalBody.innerHTML = '<div class="wl-grid">' + cards + '</div>';
      /* Preselect the first picker value so quick-add wires to a real variant. */
      modalBody.querySelectorAll('[data-pc] [data-picker]').forEach(function (picker) {
        var b = picker.querySelector('[data-picker-val][data-available="true"]') || picker.querySelector('[data-picker-val]');
        if (b) b.click();
      });
      if (window.initProductCards) window.initProductCards(modalBody);
    });
  }

  function openModal() {
    ensureModal();
    modalReturnFocus = document.activeElement;
    modal.hidden = false;
    document.body.classList.add('cmp-modal-open');
    modalBody.innerHTML = '<p class="cmp-modal__loading">Loading your wishlist…</p>';
    void modal.offsetWidth;
    modal.classList.add('is-open');
    renderModal();
    var c = modal.querySelector('.cmp-modal__close'); if (c) c.focus();
  }

  function closeModal() {
    if (!modal) return;
    closeMenu();
    editingId = null;
    modal.classList.remove('is-open');
    document.body.classList.remove('cmp-modal-open');
    setTimeout(function () { if (modal && !modal.classList.contains('is-open')) modal.hidden = true; }, 260);
    if (modalReturnFocus && typeof modalReturnFocus.focus === 'function') modalReturnFocus.focus();
  }

  function onOpenClick(e) {
    var t = e.target.closest('[data-wishlist-open]');
    if (!t) return;
    e.preventDefault();
    openModal();
  }

  function init() {
    document.addEventListener('click', onToggleClick, true);
    document.addEventListener('click', onOpenClick);
    document.addEventListener('click', onManagerClick);
    document.addEventListener('keydown', onDocKeydown);
    document.addEventListener('focusout', onManagerFocusout);
    document.addEventListener('wishlist:change', function () {
      syncAllToggles();
      syncCounts();
      syncHeader();
      renderManager();
      renderModal();
    });
    /* Cross-tab sync */
    window.addEventListener('storage', function (e) {
      if (e.key === KEY || e.key === LEGACY_KEY) {
        syncAllToggles();
        syncCounts();
        syncHeader();
        renderManager();
        renderModal();
      }
    });
    importShared();
    syncAllToggles();
    syncCounts();
    syncHeader();
    renderManager();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.NerdioWishlist = {
    read: read, has: has, add: add, remove: remove, toggle: toggle,
    clear: clearActive, count: count, totalCount: totalCount,
    groups: function () { return readState().groups; },
    createGroup: createGroup, renameGroup: renameGroup, deleteGroup: deleteGroup, setActive: setActive,
    duplicateGroup: duplicateGroup, shareGroup: shareGroup,
    open: openModal, syncToggles: syncAllToggles, MAX_GROUPS: MAX_GROUPS
  };
})();
